import { ApiFormattedResponse } from "@models/api_formatted_response"
import { mutate, query } from "@utils/graphql"
import {
  Action,
  ActionsPaginatedResponse,
  AiAnswerData,
  AiGuidance,
  AiGuidancePaginatedResponse,
  AiKnowledgeSource,
  AiKnowledgeSourceContent,
  AiKnowledgeSourceContentPaginatedResult,
  AiKnowledgeSourcePaginatedResponse,
  AiKnowledgeSourcesSourceTypes,
  AiKnowledgeSourcesStatus,
  AiProfile,
  AiProfileResponse,
  DeleteTestConversationInput,
  DeleteTestConversationResponse,
  MessageForAiSimulationData,
  MessageForAiSimulationResponse,
  TestAiWithConversationInput,
  TestAiWithConversationResponse
} from "__generated__/graphql"
import deleteTestConversation from "__generated__/queries/mutations/deleteTestConversation"
import testAiWithConversation from "__generated__/queries/mutations/testAiWithConversation"
import getActions from "__generated__/queries/queries/getActions"
import getAiGuidanceList from "__generated__/queries/queries/getAiGuidanceList"
import getAiProfile from "__generated__/queries/queries/getAiProfile"
import getAllAiKnowledgeSourceContent from "__generated__/queries/queries/getAllAiKnowledgeSourceContent"
import getAllAiKnowledgeSources from "__generated__/queries/queries/getAllAiKnowledgeSources"
import getMessageForAiSimulation from "__generated__/queries/queries/getMessageForAiSimulation"

export async function getAiProfileQuery(
  customerId: string
): Promise<ApiFormattedResponse<AiProfile>> {
  const response = await query<
    { getAiProfile?: AiProfileResponse },
    { customerId: string }
  >({
    query: getAiProfile,
    variables: { customerId }
  })
  if (!response.isSuccess || !response.data?.getAiProfile?.data) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response.data.getAiProfile.data
  }
}

export async function testAiWithConversationMutation(
  data: TestAiWithConversationInput
): Promise<ApiFormattedResponse<AiAnswerData>> {
  const response = await mutate<{
    testAiWithConversation?: TestAiWithConversationResponse
  }>({
    mutation: testAiWithConversation,
    variables: { data }
  })
  if (!response.isSuccess || !response.data?.testAiWithConversation?.data) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response.data.testAiWithConversation.data
  }
}

export async function getMessageForAiSimulationQuery(
  customerId: string,
  messageId: string
): Promise<ApiFormattedResponse<MessageForAiSimulationData>> {
  const response = await query<
    { getMessageForAiSimulation?: MessageForAiSimulationResponse },
    { customerId: string; messageId: string }
  >({
    query: getMessageForAiSimulation,
    variables: { customerId, messageId }
  })
  const envelope = response.data?.getMessageForAiSimulation
  if (!response.isSuccess || !envelope?.success || !envelope.data) {
    return {
      isSuccess: false,
      errorMessage: envelope?.message || response.errorMessage
    }
  }
  return {
    isSuccess: true,
    data: envelope.data
  }
}

export type ActionsPageResult = {
  docs: Action[]
  hasNextPage: boolean
  totalRecords: number
}

export async function getActionsForCustomerQuery(
  customerId: string,
  pageNumber: number,
  pageSize: number
): Promise<ApiFormattedResponse<ActionsPageResult>> {
  const response = await query<{ getActions?: ActionsPaginatedResponse }>({
    query: getActions,
    variables: { input: { customerId, pageNumber, pageSize } }
  })
  if (!response.isSuccess || !response.data?.getActions?.data) {
    return { ...response, data: undefined }
  }
  const data = response.data.getActions.data
  return {
    isSuccess: true,
    data: {
      docs: (data.docs || []).filter((action): action is Action => !!action),
      hasNextPage: !!data.hasNextPage,
      totalRecords: data.totalRecords || 0
    }
  }
}

export async function getAiGuidanceListQuery(
  customerId: string,
  search?: string
): Promise<ApiFormattedResponse<AiGuidance[]>> {
  const response = await query<{
    getAiGuidanceList?: AiGuidancePaginatedResponse
  }>({
    query: getAiGuidanceList,
    variables: {
      data: {
        customerId,
        pageNumber: 1,
        pageSize: 500,
        ...(search && { search })
      }
    }
  })
  if (!response.isSuccess || !response.data?.getAiGuidanceList?.data) {
    return { ...response, data: [] }
  }
  return {
    isSuccess: true,
    data: (response.data.getAiGuidanceList.data.docs || []).filter(
      (guidance): guidance is AiGuidance => !!guidance
    )
  }
}

export type AiKnowledgeSourcesPageParams = {
  customerId: string
  pageNumber: number
  pageSize: number
  search?: string
  status?: AiKnowledgeSourcesStatus
  sourceType?: AiKnowledgeSourcesSourceTypes
}

export type AiKnowledgeSourcesPageResult = {
  docs: AiKnowledgeSource[]
  totalDocs: number
  hasNextPage: boolean
  hasPrevPage: boolean
}

export async function getAllAiKnowledgeSourcesQuery(
  params: AiKnowledgeSourcesPageParams
): Promise<ApiFormattedResponse<AiKnowledgeSourcesPageResult>> {
  const response = await query<{
    getAllAiKnowledgeSources?: AiKnowledgeSourcePaginatedResponse
  }>({
    query: getAllAiKnowledgeSources,
    variables: {
      data: {
        customer: params.customerId,
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
        ...(params.search && { search: params.search }),
        ...(params.status && { status: params.status }),
        ...(params.sourceType && { sourceType: params.sourceType })
      }
    }
  })
  if (!response.isSuccess || !response.data?.getAllAiKnowledgeSources?.data) {
    return { ...response, data: undefined }
  }
  const data = response.data.getAllAiKnowledgeSources.data
  return {
    isSuccess: true,
    data: {
      docs: (data.docs || []).filter(
        (source): source is AiKnowledgeSource => !!source
      ),
      totalDocs: data.totalDocs || 0,
      hasNextPage: !!data.hasNextPage,
      hasPrevPage: !!data.hasPrevPage
    }
  }
}

export async function getAiKnowledgeSourceContentQuery(
  customerId: string,
  aiKnowledgeSourceId: string
): Promise<ApiFormattedResponse<AiKnowledgeSourceContent[]>> {
  const response = await query<{
    getAllAiKnowledgeSourceContent?: AiKnowledgeSourceContentPaginatedResult
  }>({
    query: getAllAiKnowledgeSourceContent,
    variables: {
      data: {
        aiKnowledgeSourceId,
        customer: customerId,
        pageNumber: 1,
        pageSize: 50
      }
    }
  })
  const envelope = response.data?.getAllAiKnowledgeSourceContent
  if (!response.isSuccess || !envelope?.success || !envelope.data) {
    return {
      isSuccess: false,
      errorMessage: envelope?.message || response.errorMessage
    }
  }
  return {
    isSuccess: true,
    data: (envelope.data.docs || []).filter(
      (content): content is AiKnowledgeSourceContent => !!content
    )
  }
}

export async function deleteTestConversationMutation(
  data: DeleteTestConversationInput
): Promise<ApiFormattedResponse<boolean>> {
  const response = await mutate<{
    deleteTestConversation?: DeleteTestConversationResponse
  }>({
    mutation: deleteTestConversation,
    variables: { data }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response.data?.deleteTestConversation?.data ?? false
  }
}
